import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {type FC, memo, useEffect, useRef, useState} from 'react';

import {
  useNavContext,
  useNavUpdaterContext,
} from '../../../components/NavContextProvider';
import {urlPrefixed} from '../../../config';
import {HomePageContextProvider} from '../../../pages/HomePage/HomePageContextProvider';
import {HomePageSidebar} from '../../../pages/HomePage/HomePageSidebar';
import {useViewer} from '../../../state/viewer/hooks';
import {isInIframe} from '../../../util/window';
import {FloatingSidebar} from '../../PageWithSidebar/FloatingSidebar';
import WBLogo from '../../WBLogo';
import NavLeftDefault from './NavLeftDefault';
import NavLeftInIframe from './NavLeftInIframe';

// TODO(forge): The legacy global nav toggle/floating sidebar below only renders in the non-Forge (standalone) entry point; dedicated deployments still use it. Remove entirely once dedicated is switched over to Forge shell mode. In shell mode the home page renders ForgeHomeSidebar instead.
const NavLeftComp: FC = () => {
  const globalNavTriggerRef = useRef<HTMLButtonElement>(null);
  const {globalNav, isCreatingProject, isCreatingTeam} = useNavContext();
  const {updateGlobalNav} = useNavUpdaterContext();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTooltipTimeout = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  const openTooltipWithDelay = () => {
    clearTooltipTimeout();
    tooltipTimeoutRef.current = setTimeout(() => setIsTooltipOpen(true), 500);
  };

  const closeTooltip = () => {
    clearTooltipTimeout();
    setIsTooltipOpen(false);
  };

  useEffect(() => clearTooltipTimeout, []);

  const viewer = useViewer();
  const isLoggedIn = !!viewer;
  if (!isLoggedIn) {
    return (
      <div className="nav-left-container">
        <WBLogo />
        {isInIframe() ? <NavLeftInIframe /> : <NavLeftDefault />}
      </div>
    );
  }

  return (
    <div className="nav-left-container">
      <TooltipDeprecated
        position="bottom center"
        open={isTooltipOpen}
        trigger={
          <WBLogo
            data-test="wb-logo"
            aria-label={`${
              globalNav.isOpen ? 'Close' : 'Open'
            } global navigation`}
            ref={globalNavTriggerRef}
            onClick={e => {
              window.analytics?.track('Nav logo clicked');
              closeTooltip();
              if (isInIframe()) {
                e.preventDefault();
                window.open(urlPrefixed());
              } else {
                updateGlobalNav({
                  isOpen: !globalNav.isOpen,
                  shouldAnimate: true,
                });
              }
            }}
            onMouseEnter={() => {
              updateGlobalNav({isTriggerHovered: true});
              openTooltipWithDelay();
            }}
            onMouseLeave={() => {
              updateGlobalNav({isTriggerHovered: false});
              closeTooltip();
            }}
          />
        }>
        {globalNav.isOpen ? 'Close' : 'Open'} global navigation
      </TooltipDeprecated>
      {!globalNav.isInline && (
        <FloatingSidebar
          isOpen={globalNav.isOpen}
          triggerRef={globalNavTriggerRef}
          close={() => updateGlobalNav({isOpen: false, shouldAnimate: true})}
          shouldAnimate={globalNav.shouldAnimate}
          isCreatingTeamOrProject={isCreatingTeam || isCreatingProject}>
          {globalNav.isOpen && (
            <HomePageContextProvider>
              <HomePageSidebar />
            </HomePageContextProvider>
          )}
        </FloatingSidebar>
      )}
      {isInIframe() ? <NavLeftInIframe /> : <NavLeftDefault />}
    </div>
  );
};

const NavLeft = memo(NavLeftComp);

export default NavLeft;
